import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../modules';
import { uploadFile } from '../../modules/editor/service'
import { editContent, applyFilesize, toggleContentsTargetLoading } from '../../modules/editor/actions';
import useAlert from '../../hooks/useAlert';
import { isImageType, getImageMeta } from '../../util/file';

import image_upload from '../../assets/images/icon/image_upload.png';
import ImageLibrary from './ImageLibrary';

import { MAX_FILE_SIZE, SMALL_IMAGE_HEIGHT } from '../../constants'; 

type UploadProps = {
	target: string;
	columnCount: number;
	isDummy?: boolean;
	imageInfo?: HTMLImageElement;
	maxWidth: number;
};

function Upload({ target, columnCount, isDummy, imageInfo, maxWidth }: UploadProps) {
	const dispatch = useDispatch();
	const Alert = useAlert();
	
	const { email } = useSelector((state: RootState) => state.editor);
	const [showLibarary, setShowLibrary] = useState(false);
	const [isSmallImage, setIsSmallImage] = useState(false);
	const [inputClicked, setInputClicked] = useState(false);

	const screenMode = useSelector((state: RootState) => state.editor.ui.screenMode);
	const emailType = useSelector((state: RootState) => state.editor.email.type);
	const emailSubType = useSelector((state: RootState) => state.editor.email.subType);

	let testGroup = undefined;
	if (emailType === 2 && emailSubType === 4) {
		testGroup = useSelector((state: RootState) => state.editor.testGroup);
	}

	useEffect(() => {
		if (imageInfo && screenMode === 'desktop') {
			setIsSmallImage(Boolean(imageInfo?.height <= SMALL_IMAGE_HEIGHT));
			let imageStyleTarget = target.replace('/content/', '/style/').replace('/imagePath', '/image');
			dispatch(editContent({ target: `${imageStyleTarget}/originalWidth`, data: `${imageInfo?.width}`}));
		}
	}, [imageInfo]);

	const fileInput = React.useRef<HTMLInputElement>(null);

	const onImageUpload = async (e: any) => {	
		if (fileInput.current) {
			if (fileInput.current.files) {
				upload(fileInput.current.files)
			}
		}
	}

	const upload = async (files:any) => {		
		// 형식 확인
		if (!isImageType(files[0]?.type)) {
			Alert.error(Alert?.msg?.FILE_IMAGE_TYPE);
			return;
		}
	
		// maxsize 확인
		if (files[0].size >= MAX_FILE_SIZE) {
			Alert.error(Alert?.msg?.FILE_MAX_SIZE);
			return;
		}
		
		const formData = new FormData();
		Array.from(files).forEach((item: any) => {
			formData.append('uploadfile', item);
		});

		try {
			dispatch(toggleContentsTargetLoading({ target, isLoading: true }));
			const { totalSize, url }:any = await uploadFile(email.id, formData, testGroup);
			
			// 이미지 사이즈 확인 & 정렬 조정
			const imageMeta = await getImageMeta(url);
			dispatch(toggleContentsTargetLoading({ target, isLoading: false }));
			if (imageMeta?.width < maxWidth) {
				let imageStyleTarget = target.replace('/content/', '/style/').replace('/imagePath', '/image');
				dispatch(editContent({ target: `${imageStyleTarget}/textAlign`, data: 'center' }));
				dispatch(editContent({ target: `${imageStyleTarget}/fullWidth`, data: '' }));
			}
			
			if (url !== undefined) {
				dispatch(editContent({ target: target, data: url }));
				dispatch(applyFilesize(totalSize));
			}			
		} catch (error) {
			Alert.error(error?.response?.data);
			console.error(error);
		}

	}

	const onDrop = (e:any) => {		
		e.preventDefault();
		const file = e.dataTransfer.items[0].getAsFile();		
		upload([file]);
	}
	
	const onDragOver = (e:any) => {		
		e.preventDefault();		
	}

	const openLibrary = () => {		
		setShowLibrary(true);
	}

	const closeLibrary = () => {		
		setShowLibrary(false);
	}

	const onInputClick = () => {	
		setInputClicked(true);
		if (!inputClicked) {
			fileInput.current?.click();
		}

		setTimeout(() => {
			setInputClicked(false);
		}, 500);
	}

	return (
		<>
		<div 
			className="common-upload"
			onDrop={onDrop}
			onDragOver={onDragOver}
		>
			<div className="zone" onClick={onInputClick}>
				<div className="cont">
					{
						isDummy && (
							<img src={image_upload} alt="image_upload" width="58"/>
						)
					}
					{
						isSmallImage ? (
							<p className="short">여기를 클릭하거나 파일을 끌어다 놓으세요.</p>
						) : (
							(
								columnCount === 1 &&
								<p>
									여기를 클릭하거나 파일을 끌어다 놓으세요.<br />
									5MB 이하, 권장 사이즈 가로 630px
								</p>
							) || (
								columnCount === 2 &&
								<p className="col-2">
									여기를 클릭하거나 파일을 끌어다 놓으세요<br />
									5MB 이하, 권장 사이즈 가로 315px
								</p>
							)
						)
					}
				</div>
			</div>
			<input type="file" ref={fileInput} onChange={onImageUpload} className="blind"  />
			{/* {!isSmallImage && <button className="library-btn btn small" onClick={openLibrary}>Unsplash에서 추가</button>} */}
		</div>
		{
			showLibarary && (
				<ImageLibrary target={target} close={closeLibrary} />
			)
		}
		</>
	);
}

export default Upload;